// Macro Compass — one of three widgets on the dashboard's macro section.
//
// Fetches ``GET /api/v1/macro/current-regime`` and plots the active
// regime as a single dot on a four-quadrant Recharts scatter chart.
// The X axis is the growth Z-score (``g_score``) and the Y axis is
// the growth-momentum Z-score (``mg_score``). Both axes share a fixed
// [-3, +3] domain so the (0, 0) crosshair always sits in the centre,
// which means the marker auto-places itself in the mathematically
// correct quadrant — no conditional UI logic required:
//
//             Momentum high (mg_score > 0)
//                  ▲
//      Recovery    │    Expansion
//   ───────────────┼───────────────►  Growth high (g_score > 0)
//      Contraction │    Slowdown
//                  ▼
//             Momentum low (mg_score < 0)
//
//   Top-right (g > 0, mg > 0): Expansion   — growing & accelerating
//   Top-left  (g < 0, mg > 0): Recovery    — accelerating out of contraction
//   Bot-right (g > 0, mg < 0): Slowdown    — still growing but decelerating
//   Bot-left  (g < 0, mg < 0): Contraction — shrinking & decelerating
//
// The dot uses live (g_score, mg_score) when available; otherwise we
// fall back to the centroid of the regime with the highest ensemble
// probability so the widget still renders something useful while the
// feature row is being recomputed by the worker. The four quadrants
// are tinted with the same regime palette used by the Cycle Trajectory
// widget so the user can pattern-match across the two charts without
// a separate legend lookup.

import { useEffect, useState } from 'react';
import {
    CartesianGrid,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Scatter,
    ScatterChart,
    Tooltip,
    XAxis,
    YAxis,
    ZAxis,
} from 'recharts';

import {
    formatMonthEnd,
    macroApi,
    type MacroRegimeState,
} from '../../../api/macro';

interface QuadrantColors {
    fill: string;
    border: string;
    text: string;
}

// Border / text / fill colours per regime. ``border`` matches the
// solid stroke colour used by the Cycle Trajectory area chart so the
// two widgets are visually consistent. ``fill`` is the tint applied
// to the regime's quadrant on the scatter plot.
const REGIME_PALETTE: Record<string, QuadrantColors> = {
    Expansion: {
        fill: 'rgba(16, 185, 129, 0.14)', // emerald-500
        border: '#10b981',
        text: 'text-emerald-700 dark:text-emerald-300',
    },
    Recovery: {
        fill: 'rgba(14, 165, 233, 0.14)', // sky-500
        border: '#0ea5e9',
        text: 'text-sky-700 dark:text-sky-300',
    },
    Slowdown: {
        fill: 'rgba(245, 158, 11, 0.14)', // amber-500
        border: '#f59e0b',
        text: 'text-amber-700 dark:text-amber-300',
    },
    Contraction: {
        fill: 'rgba(239, 68, 68, 0.14)', // red-500
        border: '#ef4444',
        text: 'text-red-700 dark:text-red-300',
    },
};

// Centroid (g, mg) per quadrant in score units — used as a fallback
// when the live composite scores are unavailable. Coordinates are
// in the Z-score domain emitted by the feature engine.
//
//   Expansion  : g > 0, mg > 0 → top-right
//   Recovery   : g < 0, mg > 0 → top-left
//   Slowdown   : g > 0, mg < 0 → bottom-right
//   Contraction: g < 0, mg < 0 → bottom-left
const QUADRANT_CENTROID: Record<string, { g: number; mg: number }> = {
    Expansion: { g: 1, mg: 1 },
    Recovery: { g: -1, mg: 1 },
    Slowdown: { g: 1, mg: -1 },
    Contraction: { g: -1, mg: -1 },
};

// Fixed Recharts domain on both axes. Z-scores typically sit inside
// [-3, +3], so this keeps the (0, 0) crosshair perfectly centred
// regardless of the live data and removes the need for any manual
// projection — Recharts maps (g_score, mg_score) → pixels for us.
const DOMAIN: [number, number] = [-3, 3];
const TICKS = [-3, -2, -1, 0, 1, 2, 3];

// Map the (g, mg) sign-pair to a regime label. This is what makes the
// marker "auto-place itself" in the mathematically correct quadrant.
function quadrantOf(g: number, mg: number): keyof typeof REGIME_PALETTE {
    if (g >= 0 && mg >= 0) return 'Expansion';
    if (g < 0 && mg >= 0) return 'Recovery';
    if (g >= 0 && mg < 0) return 'Slowdown';
    return 'Contraction';
}

export const MacroCompass = () => {
    const [state, setState] = useState<MacroRegimeState | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const result = await macroApi.getCurrentRegime();
                if (!cancelled) setState(result);
            } catch (err) {
                if (!cancelled) {
                    setError(
                        err instanceof Error ? err.message : 'Failed to load.',
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (loading) {
        return (
            <div className="surface-card-skeleton h-80" aria-busy>
                <p className="muted text-sm">Loading macro regime…</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="surface-card-error">
                <p className="font-bold">Current Macro Regime unavailable</p>
                <p className="text-sm mt-1">{error}</p>
            </div>
        );
    }

    if (!state) {
        return (
            <div className="surface-card">
                <h3 className="section-title">Current Macro Regime</h3>
                <p className="muted text-sm mt-2">
                    Macro regime data is still warming up. Check back in a few
                    minutes once the ingest worker has produced its first
                    classification.
                </p>
            </div>
        );
    }

    // The card header always reflects the regime with the highest
    // ensemble probability, which may briefly drift from the backend's
    // ``regime_label`` between worker runs (e.g. the label is updated
    // by a separate stage of the pipeline). Deriving from the
    // probabilities keeps the headline regime + confidence consistent
    // with the per-regime breakdown shown below the chart.
    const regimeProbs: readonly (readonly [string, number])[] = [
        ['Expansion', state.probabilities.expansion],
        ['Recovery', state.probabilities.recovery],
        ['Slowdown', state.probabilities.slowdown],
        ['Contraction', state.probabilities.contraction],
    ];
    const [topRegime, topProb] = regimeProbs.reduce((best, curr) =>
        curr[1] > best[1] ? curr : best,
    );

    const palette = REGIME_PALETTE[topRegime] ?? REGIME_PALETTE.Expansion;

    // Prefer live composite scores; fall back to the top regime's
    // centroid when the feature row hasn't caught up yet.
    const g =
        state.indicators.g_score ??
        QUADRANT_CENTROID[topRegime]?.g ??
        0;
    const mg =
        state.indicators.mg_score ??
        QUADRANT_CENTROID[topRegime]?.mg ??
        0;

    // The marker's quadrant is derived from the sign of (g, mg) — no
    // conditional rendering needed; Recharts plots the point at the
    // exact (x, y) and it falls into the correct quadrant on its own.
    const dotRegime = quadrantOf(g, mg);
    const dotPalette = REGIME_PALETTE[dotRegime] ?? palette;

    // Format the data for the Recharts ScatterChart. A single-point
    // series is enough — we just want one dot at (g_score, mg_score).
    const chartData = [
        {
            x: g,
            y: mg,
            regime: dotRegime,
            confidence: (topProb * 100).toFixed(1),
        },
    ];

    const confidencePct = Math.round(topProb * 100);

    return (
        <div
            className="surface-card"
            style={{ borderColor: palette.border }}
        >
            <div className="flex items-baseline justify-between gap-3">
                <div>
                    <h3 className="section-title">Current Macro Regime Based on Confidence Level</h3>
                    <p className="muted text-xs">
                        As of · {formatMonthEnd(state.date)}
                    </p>
                    {state.last_updated_utc && (
                        <p className="muted text-[11px]">
                            Last refreshed{' '}
                            {new Date(
                                state.last_updated_utc,
                            ).toLocaleString(undefined, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                            })}
                        </p>
                    )}
                </div>
                <div className="text-right">
                    <p className={`text-2xl font-black ${palette.text}`}>
                        {topRegime}
                    </p>
                    <p className="muted text-xs">
                        {confidencePct}% confidence
                    </p>
                </div>
            </div>

            <div
                className="mt-4 w-full h-72"
                role="img"
                aria-label={`Current regime: ${topRegime} (g_score=${g.toFixed(2)}, mg_score=${mg.toFixed(2)})`}
            >
                <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart
                        margin={{ top: 16, right: 24, bottom: 28, left: 24 }}
                    >
                        {/* Quadrant tints — same palette as Cycle Trajectory.
                            Top-left = Recovery (blue), top-right = Expansion
                            (green), bottom-left = Contraction (red),
                            bottom-right = Slowdown (orange). */}
                        <ReferenceArea
                            x1={0}
                            x2={DOMAIN[1]}
                            y1={0}
                            y2={DOMAIN[1]}
                            fill={REGIME_PALETTE.Expansion.fill}
                            fillOpacity={1}
                            stroke="none"
                            label={{
                                value: 'Expansion',
                                position: 'insideTopRight',
                                fill: REGIME_PALETTE.Expansion.border,
                                fontWeight: 700,
                                fontSize: 12,
                            }}
                        />
                        <ReferenceArea
                            x1={DOMAIN[0]}
                            x2={0}
                            y1={0}
                            y2={DOMAIN[1]}
                            fill={REGIME_PALETTE.Recovery.fill}
                            fillOpacity={1}
                            stroke="none"
                            label={{
                                value: 'Recovery',
                                position: 'insideTopLeft',
                                fill: REGIME_PALETTE.Recovery.border,
                                fontWeight: 700,
                                fontSize: 12,
                            }}
                        />
                        <ReferenceArea
                            x1={DOMAIN[0]}
                            x2={0}
                            y1={DOMAIN[0]}
                            y2={0}
                            fill={REGIME_PALETTE.Contraction.fill}
                            fillOpacity={1}
                            stroke="none"
                            label={{
                                value: 'Contraction',
                                position: 'insideBottomLeft',
                                fill: REGIME_PALETTE.Contraction.border,
                                fontWeight: 700,
                                fontSize: 12,
                            }}
                        />
                        <ReferenceArea
                            x1={0}
                            x2={DOMAIN[1]}
                            y1={DOMAIN[0]}
                            y2={0}
                            fill={REGIME_PALETTE.Slowdown.fill}
                            fillOpacity={1}
                            stroke="none"
                            label={{
                                value: 'Slowdown',
                                position: 'insideBottomRight',
                                fill: REGIME_PALETTE.Slowdown.border,
                                fontWeight: 700,
                                fontSize: 12,
                            }}
                        />

                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="currentColor"
                            opacity={0.15}
                        />

                        <XAxis
                            type="number"
                            dataKey="x"
                            name="Growth"
                            domain={DOMAIN}
                            ticks={TICKS}
                            tick={{ fontSize: 11, fill: 'currentColor' }}
                            label={{
                                value: 'Growth',
                                position: 'insideBottom',
                                offset: -10,
                                fontSize: 12,
                                fill: 'currentColor',
                            }}
                        />
                        <YAxis
                            type="number"
                            dataKey="y"
                            name="Momentum"
                            domain={DOMAIN}
                            ticks={TICKS}
                            tick={{ fontSize: 11, fill: 'currentColor' }}
                            label={{
                                value: 'Momentum',
                                angle: -90,
                                position: 'insideLeft',
                                offset: 10,
                                fontSize: 12,
                                fill: 'currentColor',
                            }}
                        />
                        {/* Fixed dot size regardless of value count. */}
                        <ZAxis range={[180, 180]} />

                        {/* Crosshair through the origin (0, 0). */}
                        <ReferenceLine
                            x={0}
                            stroke="currentColor"
                            strokeOpacity={0.45}
                        />
                        <ReferenceLine
                            y={0}
                            stroke="currentColor"
                            strokeOpacity={0.45}
                        />

                        <Tooltip
                            cursor={{ strokeDasharray: '3 3' }}
                            contentStyle={{
                                background: 'rgba(15, 23, 42, 0.97)',
                                border: '1px solid #334155',
                                borderRadius: 8,
                                fontSize: 12,
                            }}
                            itemStyle={{ color: '#f8fafc' }}
                            labelStyle={{
                                color: '#cbd5e1',
                                fontWeight: 700,
                            }}
                            formatter={(value: number, name: string) => [
                                value.toFixed(2),
                                name,
                            ]}
                        />

                        <Scatter
                            data={chartData}
                            fill={dotPalette.border}
                            stroke="white"
                            strokeWidth={2}
                            isAnimationActive={false}
                        />
                    </ScatterChart>
                </ResponsiveContainer>
            </div>

            <dl className="mt-4 grid grid-cols-4 gap-2 text-xs">
                {regimeProbs.map(([label, p]) => (
                    <div key={label} className="text-center">
                        <dt className="muted">{label}</dt>
                        <dd className="font-bold tabular-nums">
                            {Math.round(p * 100)}%
                        </dd>
                    </div>
                ))}
            </dl>
        </div>
    );
};

export default MacroCompass;
